import logoUrl from "../assets/ui/logo.webp";
import type { GraphicsQuality } from "../render/scene";
import { isMobileDevice, requestFullscreen } from "./fullscreen";
import { openSettingsModal } from "./settingsModal";
import type { SolitaireSettings } from "./settings";

/**
 * Menu principal en surimpression : logo du jeu (memes assets que
 * dkg-intro/assets/logo-solitaire.webp, source canonique de l'icone d'app),
 * boutons cuir/parchemin. Le decor 3D de la taverne tourne deja derriere :
 * on s'assoit a la table, on n'entre pas dans un ecran abstrait.
 */
export class MainMenu {
  private el: HTMLDivElement;

  constructor(
    root: HTMLElement,
    onPlay: () => void,
    onGraphicsChange: (quality: GraphicsQuality) => void,
    onSettingsChange: (settings: SolitaireSettings) => void,
  ) {
    this.el = document.createElement("div");
    this.el.id = "main-menu";
    this.el.innerHTML = `
      <div class="menu-panel">
        <h1 class="menu-logo-wrap"><img class="menu-logo" src="${logoUrl}" alt="Le Solitaire" /></h1>
        <p class="menu-tagline">Un jeu de cartes, une chope, et tout le temps du monde.</p>
        <div class="menu-buttons">
          <button class="wood-button leather primary" id="menu-play">Jouer</button>
          <button class="wood-button leather secondary" id="menu-rules">Règles</button>
          <button class="wood-button leather secondary" id="menu-settings">Réglages</button>
          <button class="wood-button leather secondary" id="menu-credits">Crédits</button>
        </div>
      </div>
      <div class="menu-modal" id="menu-modal" hidden>
        <div class="parchment-panel">
          <div id="menu-modal-content"></div>
          <button class="wood-button" id="menu-modal-close">Fermer</button>
        </div>
      </div>
    `;
    root.appendChild(this.el);

    this.el.querySelector<HTMLButtonElement>("#menu-play")!.addEventListener("click", () => {
      // Meme geste que le clic : le navigateur exige une interaction utilisateur
      // directe pour accorder le plein ecran, ce clic en est une.
      if (isMobileDevice()) void requestFullscreen();
      this.hide();
      onPlay();
    });
    this.el.querySelector<HTMLButtonElement>("#menu-rules")!.addEventListener("click", () => {
      this.openModal(RULES_HTML);
    });
    this.el.querySelector<HTMLButtonElement>("#menu-settings")!.addEventListener("click", () => {
      openSettingsModal(root, onGraphicsChange, onSettingsChange);
    });
    this.el.querySelector<HTMLButtonElement>("#menu-credits")!.addEventListener("click", () => {
      this.openModal(CREDITS_HTML);
    });
    this.el.querySelector<HTMLButtonElement>("#menu-modal-close")!.addEventListener("click", () => {
      this.el.querySelector<HTMLElement>("#menu-modal")!.hidden = true;
    });
  }

  show(): void {
    this.el.classList.remove("hidden");
  }

  hide(): void {
    this.el.classList.add("hidden");
  }

  private openModal(html: string): void {
    this.el.querySelector<HTMLElement>("#menu-modal-content")!.innerHTML = html;
    this.el.querySelector<HTMLElement>("#menu-modal")!.hidden = false;
  }
}

/** Regles du Klondike, aussi affichees par la modale in-game du HUD. */
export const RULES_HTML = `
  <h2>Règles du jeu</h2>
  <p>Le but : bâtir les <strong>4 fondations</strong> (en haut à droite), une
  par enseigne, de l'<strong>As au Roi</strong> dans l'ordre croissant.</p>
  <p>Dans les <strong>7 colonnes</strong>, on empile en ordre décroissant en
  alternant les couleurs (rouge sur noir, noir sur rouge). Un groupe de cartes
  déjà ordonné se déplace d'un seul geste. Seul un <strong>Roi</strong> peut
  occuper une colonne vide.</p>
  <p>La <strong>pioche</strong> (en haut à gauche) retourne 3 cartes à la fois
  sur la défausse (ou 1 seule, dans les réglages) ; seule la carte du dessus
  est jouable. Pioche épuisée : cliquez pour la reformer, autant de fois que
  nécessaire.</p>
  <p>Gestes utiles : <strong>glissez</strong> une carte vers sa destination,
  ou <strong>double-cliquez</strong> pour l'envoyer directement sur sa
  fondation. <strong>Annuler</strong> est illimité, et le bouton
  <strong>Indice</strong> souffle un coup jouable.</p>
`;

const CREDITS_HTML = `
  <h2>Crédits</h2>
  <p><strong>Le Solitaire</strong> — un jeu de cartes dans l'univers du Taverneu.</p>
  <p>Rendu 3D : <a href="https://threejs.org/" target="_blank" rel="noreferrer">Three.js</a>.
  Cartes et textures générées par code (aucun asset externe au runtime).</p>
  <p>Polices : Cinzel, IM Fell English, Metamorphous (SIL OFL, auto-hébergées
  via Fontsource).</p>
  <p>Décor de taverne repris des <em>Batailles du Taverneu</em> (dkg-battle).</p>
  <p>Sons de cartes et feu de cheminée : enregistrements <em>Casino
  Audio</em> par Kenney Vleugels (<a href="https://kenney.nl/" target="_blank" rel="noreferrer">kenney.nl</a>),
  domaine public (CC0). Fanfare de victoire : cynicmusic, domaine public
  (CC0). Musique : <em>Midnight Tale</em> par Kevin MacLeod
  (<a href="https://incompetech.com/" target="_blank" rel="noreferrer">incompetech.com</a>),
  Creative Commons Attribution 4.0. Le reste (boutons, blocage, murmure
  de salle) est synthétisé à la volée (Web Audio API).</p>
`;
